import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Ban } from 'lucide-react'
import apiClient from '@/api/client'
import type { ActiveOnlineOrder } from '@/types'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { PinPad } from '@/components/ui/pin-pad'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { toastHelpers } from '@/lib/toast-helpers'
import { printKotReceipts } from '@/lib/printKotReceipt'
import { parseReceiptSettings } from '@/lib/printCustomerReceipt'
import { VOID_OUTCOME_HELP, VOID_OUTCOME_LABEL, type VoidOutcome } from '@/lib/void-reasons'
import {
  CANCEL_NOTE_MAX,
  OTHER_CANCEL_CODE,
  cancelErrorMessage,
  cancelFormComplete,
  cancelReasonChips,
  isDeliveryFailure,
  suggestedCancelOutcome,
} from '@/lib/onlineOrderCancel'

// F-ONL-N6: cancel an accepted online order, or record a failed delivery.
// Two steps, like a counter void: what happened, then the manager's PIN. Every
// line is voided into the Void Log server-side and the customer is told.
export function CancelOnlineOrderDialog({
  order,
  formatCurrency,
  onClose,
  onCancelled,
}: {
  order: ActiveOnlineOrder | null
  formatCurrency: (n: number) => string
  onClose: () => void
  /** Refresh whatever lists the order sat in. */
  onCancelled: () => void
}) {
  const queryClient = useQueryClient()
  const [step, setStep] = useState<'details' | 'pin'>('details')
  const [code, setCode] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [outcome, setOutcome] = useState<VoidOutcome | null>(null)
  const [pin, setPin] = useState('')
  const [pinError, setPinError] = useState<string | null>(null)
  const [shake, setShake] = useState(false)

  const deliveryFailure = order ? isDeliveryFailure(order) : false
  const paid = order ? order.paid_amount > 0.004 : false

  useEffect(() => {
    if (!order) return
    setStep('details')
    setCode(null)
    setNote('')
    setOutcome(suggestedCancelOutcome(order.order_status))
    setPin('')
    setPinError(null)
  }, [order])

  const cancel = useMutation({
    mutationFn: () =>
      apiClient.cancelOnlineOrder(order!.id, {
        pin,
        reason_code: code ?? OTHER_CANCEL_CODE,
        note: note.trim() || undefined,
        outcome: outcome!,
      }),
    onSuccess: async (res) => {
      onCancelled()
      const d = res.data
      toastHelpers.success(
        deliveryFailure ? 'Delivery recorded as failed' : 'Order cancelled',
        `#${d?.order_number ?? order?.order_number ?? ''} — approved by ${d?.authorized_by ?? 'manager'}. The customer has been told.${
          d?.outcome === 'wasted' ? ' Recorded as waste.' : ''
        }`,
      )
      // DO NOT MAKE chits for printer stations (until the food has left the
      // building). Best-effort, like every KOT print on this page.
      if (d?.void_kots?.length) {
        try {
          const settingsRes = await queryClient.fetchQuery({
            queryKey: ['settings', 'all'],
            queryFn: () => apiClient.getAllSettings(),
          })
          await printKotReceipts(d.void_kots, parseReceiptSettings(settingsRes.data as Record<string, unknown>))
        } catch {
          /* printing never blocks the cancel */
        }
      }
      onClose()
    },
    onError: (err: unknown) => {
      const e = err as { code?: string; message?: string }
      if (e?.code === 'invalid_manager_pin') {
        setPin('')
        setPinError(cancelErrorMessage(e.code))
        setShake(true)
        window.setTimeout(() => setShake(false), 650)
        return
      }
      toastHelpers.error(deliveryFailure ? 'Could not record the failed delivery' : 'Could not cancel the order', cancelErrorMessage(e?.code, e?.message))
      setPin('')
      setStep('details')
    },
  })

  const submit = () => {
    if (pin.length === 4 && !cancel.isPending) cancel.mutate()
  }

  const title = deliveryFailure ? 'Delivery failed' : 'Cancel order'
  const chips = cancelReasonChips(deliveryFailure)
  const ready = cancelFormComplete(code ?? (note.trim() ? OTHER_CANCEL_CODE : null), outcome, note)

  return (
    <Dialog open={!!order} onOpenChange={(open) => !open && !cancel.isPending && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {title}
            {order?.order_number ? ` — #${order.order_number}` : ''}
          </DialogTitle>
          <DialogDescription>
            {order?.customer_name || 'Guest'} · {formatCurrency(order?.order_total ?? order?.total_amount ?? 0)}. Every item is
            voided into the Void Log and the customer is told it was cancelled.
          </DialogDescription>
        </DialogHeader>

        {paid ? (
          <p className="rounded-lg border border-red-200 bg-danger-surface p-3 text-sm text-danger-fg dark:border-red-900/40">
            {formatCurrency(order!.paid_amount)} has been recorded on this order. Refund it at the counter first, then cancel.
          </p>
        ) : step === 'details' ? (
          <div className="space-y-4">
            <div>
              <p className="mb-2 text-sm font-medium text-foreground">What happened?</p>
              <div className="flex flex-wrap gap-2">
                {chips.map((r) => (
                  <button
                    key={r.code}
                    type="button"
                    onClick={() => setCode((cur) => (cur === r.code ? null : r.code))}
                    className={`rounded-full border px-3 py-1 text-sm transition-colors ${
                      code === r.code
                        ? 'border-red-500 bg-danger-surface text-danger-fg'
                        : 'border-border text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
              <Textarea
                className="mt-2"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={code ? 'Add a note (optional)…' : 'Something else? Say what happened…'}
                maxLength={CANCEL_NOTE_MAX}
                rows={2}
              />
            </div>

            <div>
              <p className="mb-2 text-sm font-medium text-foreground">Was the food made?</p>
              <div className="grid grid-cols-2 gap-2">
                {(['not_made', 'wasted'] as VoidOutcome[]).map((o) => (
                  <button
                    key={o}
                    type="button"
                    onClick={() => setOutcome(o)}
                    className={`rounded-lg border p-2.5 text-left transition-colors ${
                      outcome === o ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted'
                    }`}
                  >
                    <span className="block text-sm font-medium text-foreground">{VOID_OUTCOME_LABEL[o]}</span>
                    <span className="mt-0.5 block text-xs text-muted-foreground">{VOID_OUTCOME_HELP[o]}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-center text-sm font-medium text-foreground">Enter manager PIN</p>
            <p className="text-center text-xs text-muted-foreground">
              {title} — {outcome ? VOID_OUTCOME_LABEL[outcome].toLowerCase() : ''}
            </p>
            <PinPad
              value={pin}
              onChange={(v) => {
                setPin(v)
                setPinError(null)
              }}
              onSubmit={submit}
              invalid={shake}
              disabled={cancel.isPending}
            />
            {pinError && <p className="text-center text-sm text-destructive">{pinError}</p>}
          </div>
        )}

        <DialogFooter>
          {step === 'pin' ? (
            <Button variant="outline" onClick={() => setStep('details')} disabled={cancel.isPending}>
              <ArrowLeft className="mr-1.5 h-4 w-4" />
              Back
            </Button>
          ) : (
            <Button variant="outline" onClick={onClose} disabled={cancel.isPending}>
              Keep order
            </Button>
          )}
          {!paid &&
            (step === 'details' ? (
              <Button
                variant="destructive"
                disabled={!ready}
                onClick={() => {
                  if (!code) setCode(OTHER_CANCEL_CODE)
                  setStep('pin')
                }}
              >
                Continue
              </Button>
            ) : (
              <Button variant="destructive" disabled={pin.length !== 4 || cancel.isPending} onClick={submit}>
                <Ban className="mr-1.5 h-4 w-4" />
                {cancel.isPending ? 'Cancelling…' : title}
              </Button>
            ))}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
